class VideoComponent extends HTMLElement {
  #video = null;
  #observer = null;
  #slideObserver = null;
  #isIntersecting = false;
  #swiperSlide = null;
  #mediaQuery = null;
  #onMediaQueryChange = null;

  connectedCallback() {
    const videos = this.querySelectorAll('video');

    if (videos.length === 0) return;

    if (videos.length === 2) {
      this.#mediaQuery = window.matchMedia('(max-width: 749px)');
      this.#onMediaQueryChange = () => this.#selectVideo(videos);
      this.#selectVideo(videos);
      this.#mediaQuery.addEventListener('change', this.#onMediaQueryChange);
    } else {
      this.#video = videos[0];
    }

    this.#swiperSlide = this.closest('.swiper-slide');

    // Swiper initialises separately on DOMContentLoaded, so swiper-slide-active
    // may not be set yet when this element connects. Watch the slide's class list
    // directly — deterministic across browsers and covers slide navigation.
    if (this.#swiperSlide) {
      this.#slideObserver = new MutationObserver(() => this.#evaluate());
      this.#slideObserver.observe(this.#swiperSlide, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    this.#initIntersectionObserver();
  }

  disconnectedCallback() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    if (this.#slideObserver) {
      this.#slideObserver.disconnect();
      this.#slideObserver = null;
    }

    if (this.#mediaQuery && this.#onMediaQueryChange) {
      this.#mediaQuery.removeEventListener('change', this.#onMediaQueryChange);
    }

    if (this.#video && !this.#video.paused) this.#video.pause();
  }

  #selectVideo(videos) {
    const isMobile = this.#mediaQuery.matches;
    const active = isMobile ? videos[1] : videos[0];
    const inactive = isMobile ? videos[0] : videos[1];

    if (this.#video === active) return;

    inactive.pause();
    this.#video = active;
    inactive.setAttribute('preload', 'none');

    this.#evaluate();
  }

  #evaluate() {
    const isActiveSlide = !this.#swiperSlide || this.#swiperSlide.classList.contains('swiper-slide-active');

    if (this.#isIntersecting && isActiveSlide) {
      this.#play();
    } else {
      this.#pause();
    }
  }

  #initIntersectionObserver() {
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.#isIntersecting = entry.isIntersecting;
        this.#evaluate();
      }
    }, { threshold: 0.4 });

    this.#observer.observe(this);
  }

  #play() {
    if (!this.#video) return;

    if (this.#video.preload === 'none') {
      this.#video.preload = 'metadata';
    }

    // Firefox/Safari don't start fetching until load() is called explicitly.
    if (this.#video.readyState === 0) {
      this.#video.load();
    }

    // Safari's autoplay policy requires the element to be muted.
    this.#video.muted = true;

    if (this.#video.paused) {
      this.#video.play().catch((error) => {
        console.debug('Video play failed:', error);
      });
    }
  }

  #pause() {
    if (this.#video && !this.#video.paused) this.#video.pause();
  }
}

if (!customElements.get('video-component')) {
  customElements.define('video-component', VideoComponent);
}
